import { isValidDate } from '@quickmd-llc/frontend-utils'
import { OutShipmentStatus } from 'api/generated/pwrdbyQuickMDProvidersApi.schemas'

const EM_DASH = '—'

// For date-only values (e.g. RxCreatedDate, a server-side DateOnly) — no time component.
// Rendered as MM/DD/YY in UTC so a date-only string parsed as UTC midnight doesn't shift a
// day back in negative-offset timezones. Locale pinned to en-US to guarantee MM/DD/YY order.
export function formatDate(value: string | null | undefined): string {
  if (!value) return EM_DASH
  const parsed = new Date(value)
  if (!isValidDate(parsed)) return value
  return parsed.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: '2-digit',
    timeZone: 'UTC',
  })
}

// For real timestamps (e.g. OrderCreatedDate, from a UTC DateTime) — MM/DD/YY + local time.
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return EM_DASH
  const parsed = new Date(value)
  if (!isValidDate(parsed)) return value
  return parsed.toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
  })
}

// Local time of day only, for "retrying at ..." copy.
export function formatTime(value: string | null | undefined): string {
  if (!value) return EM_DASH
  const parsed = new Date(value)
  if (!isValidDate(parsed)) return value
  return parsed.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}

// Costs come back as numbers (USD). Null/undefined renders as an em dash.
export function formatCost(value: number | null | undefined): string {
  if (value == null) return EM_DASH
  return value.toLocaleString(undefined, { style: 'currency', currency: 'USD' })
}

export function formatText(value: string | null | undefined): string {
  return value?.trim() ? value : EM_DASH
}

// The API returns carriers lowercase (e.g. "usps", "fedex"). Render them with proper
// brand casing; pass through anything we don't recognize.
const CARRIER_DISPLAY: Record<string, string> = {
  usps: 'USPS',
  ups: 'UPS',
  fedex: 'FedEx',
}

export function hasActiveLabel(order: {
  LabelThirdPartyKey?: string | null
  ShipmentStatus?: OutShipmentStatus | null
}): boolean {
  return order.LabelThirdPartyKey != null && order.ShipmentStatus !== OutShipmentStatus.voided
}

export function formatCarrier(carrier: string | null | undefined): string {
  if (!carrier || !carrier.trim()) return EM_DASH
  const normalized = carrier.trim().toLowerCase()
  return CARRIER_DISPLAY[normalized] ?? carrier
}
